"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ProductCardItem {
  id: string;
  title: Record<Lang, string>;
  badge: Record<Lang, string>;
  description: Record<Lang, string>;
  href: string;
  specs: Record<Lang, string>;
  image: string;
  imgAlt: Record<Lang, string>;
}
const recommendedProducts: ProductCardItem[] = [{
  id: "plain-dutch",
  title: {
    en: "Plain Dutch Weave",
    zh: "平纹荷兰编织丝网",
    es: "Tejido Dutch liso",
    de: "Glatte Tresse",
    fr: "Tissage hollandais uni",
    pt: "Trama holandesa lisa"
  },
  badge: {
    en: "10 µm – 100 µm",
    zh: "10 µm – 100 µm",
    es: "10 µm – 100 µm",
    de: "10 µm – 100 µm",
    fr: "10 µm – 100 µm",
    pt: "10 µm – 100 µm"
  },
  description: {
    en: "High mechanical strength with low pressure drop across high-velocity gas and ventilation streams.",
    zh: "高流速气体工况下具备高机械强度与低流阻压降，适合进气与通风保护。",
    es: "Alta resistencia mecánica con baja caída de presión en corrientes de gas de alta velocidad.",
    de: "Hohe mechanische Festigkeit bei geringem Druckverlust für Hochgeschwindigkeits-Gasströme.",
    fr: "Haute résistance mécanique et faible perte de charge sous flux de gaz à grande vitesse.",
    pt: "Alta resistência mecânica e baixa perda de carga em fluxos de gás de alta velocidade."
  },
  href: "/PlainDutchWeavePage",
  specs: {
    en: "Low Pressure Drop",
    zh: "低流阻压降",
    es: "Baja caída de presión",
    de: "Geringer Druckverlust",
    fr: "Faible perte de charge",
    pt: "Baixa perda de carga"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
  imgAlt: {
    en: "Plain Dutch weave stainless steel wire cloth for gas filtration",
    zh: "气体过滤用平纹荷兰编织不锈钢滤布",
    es: "Malla de alambre de tejido Dutch liso de acero inoxidable para filtración de gas",
    de: "Glatte Tresse Edelstahldrahtgewebe für die Gasfiltration",
    fr: "Toile métallique inox tissage hollandais uni pour filtration de gaz",
    pt: "Tela de arame inox de trama holandesa lisa para filtração de gases"
  }
}, {
  id: "twill-dutch",
  title: {
    en: "Twilled Dutch Weave",
    zh: "斜纹荷兰编织丝网",
    es: "Tejido Dutch cruzado",
    de: "Köpertresse",
    fr: "Tissage hollandais sergé",
    pt: "Trama holandesa sarja"
  },
  badge: {
    en: "1 µm – 25 µm",
    zh: "1 µm – 25 µm",
    es: "1 µm – 25 µm",
    de: "1 µm – 25 µm",
    fr: "1 µm – 25 µm",
    pt: "1 µm – 25 µm"
  },
  description: {
    en: "Tight tortuous flow paths for sub-micron particulate trapping and fine industrial gas mist elimination.",
    zh: "曲折深层过滤通道，适用于亚微米级微粒截留与工业气相微雾分离。",
    es: "Canales tortuosos para retención de partículas submicrónicas y eliminación de niebla de gas.",
    de: "Enge gewundene Strömungswege für submikrone Partikelabscheidung und feine Gasnebel.",
    fr: "Cheminements tortueux étroits pour piégeage submicronique et élimination des brouillards gazeux.",
    pt: "Caminhos tortuosos para retenção submicrométrica e eliminação de névoas de gás."
  },
  href: "/TwillDutchWeavePage",
  specs: {
    en: "Fine Demisting",
    zh: "精密除雾除尘",
    es: "Desnebulización fina",
    de: "Feine Tropfenabscheidung",
    fr: "Dévésiculage fin",
    pt: "Desnebulização fina"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-wire-mesh.webp",
  imgAlt: {
    en: "Twilled Dutch weave wire mesh for fine gas mist separation",
    zh: "精密气雾分离用斜纹荷兰编织丝网",
    es: "Malla tejida Dutch cruzada para separación fina de niebla de gas",
    de: "Köpertressedrahtgewebe für feine Gasnebelabscheidung",
    fr: "Toile métallique tissage hollandais sergé pour séparation fine de brouillard de gaz",
    pt: "Malha de trama holandesa sarja para separação fina de névoa de gás"
  }
}, {
  id: "multilayer",
  title: {
    en: "Multilayer Sintered Mesh",
    zh: "多层复合烧结网",
    es: "Malla sinterizada multicapa",
    de: "Mehrlagiges Sintergewebe",
    fr: "Toile frittée multicouche",
    pt: "Malha sinterizada multicamadas"
  },
  badge: {
    en: "2 µm – 200 µm",
    zh: "2 µm – 200 µm",
    es: "2 µm – 200 µm",
    de: "2 µm – 200 µm",
    fr: "2 µm – 200 µm",
    pt: "2 µm – 200 µm"
  },
  description: {
    en: "Rigid diffusion-bonded laminate with zero media migration, fully cleanable under cyclic pulse-jet blowback.",
    zh: "坚固的扩散复合板结构，杜绝滤材脱落，耐受周期性逆向脉冲反吹。",
    es: "Laminado rígido sinterizado sin desprendimiento de medio, lavable por pulso de aire cíclico.",
    de: "Starres diffusionsgebundenes Laminat ohne Medienabrieb, vollständig pulse-jet abreinigbar.",
    fr: "Laminé fritté rigide sans migration de fibres, nettoyable par décolmatage pneumatique cyclique.",
    pt: "Laminado sinterizado rígido sem desprendimento de meio, totalmente lavável por pulso de ar."
  },
  href: "/SinteredMultilayerMeshPage",
  specs: {
    en: "Pulse-Jet Cleanable",
    zh: "耐脉冲反吹清洗",
    es: "Lavable por pulsos",
    de: "Pulse-Jet abreinigbar",
    fr: "Décolmatage pneumatique",
    pt: "Lavável por pulsos"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
  imgAlt: {
    en: "Multilayer sintered wire mesh laminate disc for pulse-jet gas filtration",
    zh: "耐脉冲反吹气体过滤多层烧结不锈钢网板",
    es: "Disco laminado de malla sinterizada multicapa para filtración de gas",
    de: "Mehrlagige Sinterscheibe für die Gasfiltration mit Pulse-Jet-Abreinigung",
    fr: "Disque laminé fritté multicouche pour filtration de gaz décolmatable",
    pt: "Disco laminado sinterizado multicamadas para filtração de gases por pulso"
  }
}, {
  id: "cartridges",
  title: {
    en: "Filter Cartridges",
    zh: "金属滤芯与滤筒",
    es: "Cartuchos filtrantes",
    de: "Filterkerzen",
    fr: "Cartouches filtrantes",
    pt: "Cartuchos filtrantes"
  },
  badge: {
    en: "Pleated & Cylindrical",
    zh: "折波与圆柱结构",
    es: "Plisados y cilíndricos",
    de: "Plissiert & zylindrisch",
    fr: "Plissées et cylindriques",
    pt: "Plissados e cilíndricos"
  },
  description: {
    en: "Pleated pack and cylindrical elements delivering extended surface area for high-temperature exhaust and sterile vent filtration.",
    zh: "折波滤芯与圆筒结构大幅提升有效过滤面积，专用于高温排气除尘与呼吸排气除菌。",
    es: "Elementos plisados y cilíndricos que amplían el área de filtración para escapes de alta temperatura y venteo estéril.",
    de: "Plissierte und zylindrische Elemente mit vergrößerter Filterfläche für Hochtemperatur-Abgas- und Entlüftungsfiltration.",
    fr: "Éléments plissés et cylindriques offrant une surface filtrante étendue pour fumées haute température et évents.",
    pt: "Elementos plissados e cilíndricos com área de filtração ampliada para exaustão em alta temperatura e respiro estéril."
  },
  href: "/FilterCartridgesPage",
  specs: {
    en: "High Surface Area",
    zh: "大有效过滤面积",
    es: "Gran superficie",
    de: "Große Filterfläche",
    fr: "Grande surface filtrante",
    pt: "Grande área filtrante"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
  imgAlt: {
    en: "All-metal pleated filter cartridge for high temperature gas filtration",
    zh: "耐高温气体过滤全金属折叠滤芯",
    es: "Cartucho filtrante metálico plisado para filtración de gas a alta temperatura",
    de: "Ganzmetall-Filterkerze plissiert für Hochtemperatur-Gasfiltration",
    fr: "Cartouche filtrante tout métal plissée pour filtration de gaz haute température",
    pt: "Cartucho filtrante metálico plissado para filtração de gases em alta temperatura"
  }
}];
interface Copy {
  eyebrow: string;
  heading: string;
  lead: string;
  exploreLink: string;
}
const copyData: Record<Lang, Copy> = {
  en: {
    eyebrow: "Product Configurations",
    heading: "Recommended Products for Gas & Air Filtration",
    lead: "Engineered mesh structures tailored for high-temperature thermal loads, low pressure drop, and complete pulse-jet backwash cleanability.",
    exploreLink: "View Specification"
  },
  zh: {
    eyebrow: "适配产品配置",
    heading: "气体与空气过滤推荐产品",
    lead: "针对工业高温热负荷、低流阻压降要求及脉冲反吹在线清洗量身定制的金属丝网结构。",
    exploreLink: "查看规格参数"
  },
  es: {
    eyebrow: "Configuraciones de producto",
    heading: "Productos recomendados para filtración de gas y aire",
    lead: "Estructuras de malla diseñadas para cargas térmicas a alta temperatura, baja caída de presión y limpieza por pulsos.",
    exploreLink: "Ver especificación"
  },
  de: {
    eyebrow: "Produktkonfigurationen",
    heading: "Empfohlene Produkte für die Gas- und Luftfiltration",
    lead: "Gewebe- und Sinterstrukturen für thermische Hochtemperaturbelastungen, minimalen Druckverlust und vollständige Pulse-Jet-Abreinigung.",
    exploreLink: "Spezifikation ansehen"
  },
  fr: {
    eyebrow: "Configurations produits",
    heading: "Produits recommandés pour la filtration des gaz et de l'air",
    lead: "Structures de toile métallique optimisées pour fortes contraintes thermiques, faible perte de charge et décolmatage pneumatique.",
    exploreLink: "Voir les spécifications"
  },
  pt: {
    eyebrow: "Configurações de produtos",
    heading: "Produtos recomendados para filtração de gases e ar",
    lead: "Estruturas de tela metálica projetadas para altas cargas térmicas, baixa perda de carga e limpeza completa por pulsos.",
    exploreLink: "Ver especificação"
  }
};
export default function RecommendedProducts() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  return <section id="recommendedproducts" data-nav="light" className="bg-background mf-section border-b border-border text-foreground">
      <div className="mf-container">
        {/* Section Heading & Short Lead */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="text-sm font-mono uppercase tracking-wider text-muted-foreground font-semibold block mb-2">
                {c.eyebrow}
              </span>
              <h2 className="font-bold text-foreground font-default tracking-tight mf-h2">
                {c.heading}
              </h2>
            </div>
            <p className="text-muted-foreground max-w-md mf-lead">
              {c.lead}
            </p>
          </div>
        </Reveal>

        {/* 4 Recommended Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {recommendedProducts.map((item, index) => <Reveal key={item.id} delay={Math.min(index * 80, 300)} className="h-full">
              <Link to={item.href} className="group flex flex-col justify-between h-full bg-card border border-border rounded-lg overflow-hidden mf-card-hover">
                <div>
                  <div className="mf-card-media mf-ratio-card w-full relative mf-img-zoom">
                    <img src={item.image} alt={pick(lang, item.imgAlt)} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  </div>

                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <Badge variant="outline" className="font-mono text-xs font-normal tracking-tight bg-muted text-foreground">
                        {pick(lang, item.badge)}
                      </Badge>
                      <span className="font-mono text-xs text-muted-foreground">
                        {pick(lang, item.specs)}
                      </span>
                    </div>

                    <h3 className="font-semibold text-foreground font-default mb-2 text-lg mf-card-title transition-colors">
                      {pick(lang, item.title)}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {pick(lang, item.description)}
                    </p>
                  </div>
                </div>

                <div className="px-5 py-3.5 border-t border-border bg-card flex items-center justify-between text-sm font-medium text-primary">
                  <span>{c.exploreLink}</span>
                  <ArrowRight className="size-4 mf-arrow-nudge text-primary" />
                </div>
              </Link>
            </Reveal>)}
        </div>
      </div>
    </section>;
}